import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IconButton, Button, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RefreshIcon from '@mui/icons-material/Refresh';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import { selectActiveRole, selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2';
import { getPuzzleById, markPuzzleAsSolved, ChessPuzzle } from '../../../../services/chessPuzzleService';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { chessSounds, useChessSound } from '../../../../utils/chessSounds';
import './PuzzleFullscreen.css';

const PuzzleFullscreenPage: React.FC = () => {
    const { puzzleId, subscriptionId } = useParams<{ puzzleId: string; subscriptionId: string }>();
    const navigate = useNavigate();
    const activeRole = useSelector(selectActiveRole);
    const serviceProviderId = useSelector(selectEffectiveUID); // This is the service provider ID
    const loggedInUserId = serviceProviderId;
    
    const [puzzle, setPuzzle] = useState<ChessPuzzle | null>(null);
    const [loading, setLoading] = useState(true);
    const [game, setGame] = useState(new Chess());
    const [boardOrientation, setBoardOrientation] = useState<'white' | 'black'>('white');
    const [puzzleProgress, setPuzzleProgress] = useState<string[]>([]);
    const [isCorrectMove, setIsCorrectMove] = useState<boolean | null>(null);
    const [puzzleCompleted, setPuzzleCompleted] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [hintArrows, setHintArrows] = useState<Array<[string, string, string]>>([]);
    const [hintSquare, setHintSquare] = useState<string | null>(null);
    const [moveHistory, setMoveHistory] = useState<Array<{ move: string; fen: string }>>([]);
    const [isSoundEnabled, setIsSoundEnabled] = useChessSound();
    const [shakingBoard, setShakingBoard] = useState(false);

    // Load puzzle
    useEffect(() => {
        const loadPuzzle = async () => {
            if (!puzzleId || !subscriptionId) return;
            
            setLoading(true);
            try {
                console.log('Loading puzzle:', { puzzleId, serviceProviderId });
                const puzzleData = await getPuzzleById(serviceProviderId, puzzleId);
                
                if (puzzleData) {
                    setPuzzle(puzzleData);
                    const newGame = new Chess(puzzleData.fen);
                    setGame(newGame);
                    
                    // Set board orientation based on whose turn it is
                    const turnColor = newGame.turn();
                    setBoardOrientation(turnColor === 'w' ? 'white' : 'black');
                    
                    setMoveHistory([{ move: 'Start', fen: puzzleData.fen }]);
                }
            } catch (error) {
                console.error('Error loading puzzle:', error);
            } finally {
                setLoading(false);
            }
        };

        loadPuzzle();
    }, [puzzleId, subscriptionId, serviceProviderId]);

    // Generate hint - highlights the piece to move
    useEffect(() => {
        if (!puzzle || !showHint) {
            setHintSquare(null);
            return;
        }

        try {
            const solutionMoves = puzzle.moves.split(' ').filter(m => m && !m.match(/^\d+\.?$/) && !m.match(/^(1-0|0-1|1\/2-1\/2|\*)$/));
            const nextMoveIndex = puzzleProgress.length;

            console.log('🔍 Generating hint for move index:', nextMoveIndex, 'Solution moves:', solutionMoves);

            if (nextMoveIndex < solutionMoves.length) {
                const nextMove = solutionMoves[nextMoveIndex];
                const tempGame = new Chess(game.fen());
                let fromSquare: string | null = null;

                for (const move of tempGame.moves({ verbose: true })) {
                    if (move.san === nextMove || `${move.from}${move.to}${move.promotion || ''}` === nextMove) {
                        fromSquare = move.from;
                        console.log('💡 Hint: Highlighting piece at', fromSquare, 'for move', nextMove);
                        break;
                    }
                }

                if (fromSquare) {
                    setHintSquare(fromSquare);
                } else {
                    console.warn('⚠️ Could not find "from" square for hint move:', nextMove);
                    setHintSquare(null);
                }
            }
        } catch (error) {
            console.error('Error generating hint:', error);
            setHintSquare(null);
        }
    }, [puzzle, showHint, game, puzzleProgress]);

    const handleMove = (sourceSquare: string, targetSquare: string, piece: string) => {
        if (puzzleCompleted) return false;

        const gameCopy = new Chess(game.fen());
        let move = null;

        try {
            move = gameCopy.move({
                from: sourceSquare,
                to: targetSquare,
                promotion: piece[1]?.toLowerCase() === 'p' && (targetSquare[1] === '8' || targetSquare[1] === '1') ? 'q' : undefined
            });
        } catch (error) {
            console.error('Invalid move:', error);
            return false;
        }

        if (!move) return false;

        // Check if move is correct
        const solutionMoves = puzzle!.moves.split(' ').filter(m => m && !m.match(/^\d+\.?$/) && !m.match(/^(1-0|0-1|1\/2-1\/2|\*)$/));
        const expectedMove = solutionMoves[puzzleProgress.length];
        const isCorrect = move.san === expectedMove || `${move.from}${move.to}${move.promotion || ''}` === expectedMove;

        if (isCorrect) {
            // Correct move
            setIsCorrectMove(true);
            setGame(gameCopy);
            setPuzzleProgress([...puzzleProgress, move.san]);
            setMoveHistory([...moveHistory, { move: move.san, fen: gameCopy.fen() }]);
            setShowHint(false);
            
            // Play sound
            if (move.captured) {
                chessSounds.playCapture();
            } else {
                chessSounds.playMove();
            }
            chessSounds.playCorrect();
            
            if (gameCopy.isCheck()) {
                chessSounds.playCheck();
            }

            // Check if puzzle is complete
            setTimeout(() => {
                if (puzzleProgress.length + 1 >= solutionMoves.length) {
                    setPuzzleCompleted(true);
                    chessSounds.playPuzzleComplete();
                    
                    // Mark as solved
                    if (activeRole && loggedInUserId) {
                        markPuzzleAsSolved(
                            activeRole.serviceProviderId,
                            subscriptionId!,
                            puzzleId!,
                            loggedInUserId,
                            activeRole.displayName || 'User'
                        );
                    }
                } else {
                    // Computer's response
                    makeComputerMove(gameCopy, solutionMoves, puzzleProgress.length + 1);
                }
            }, 300);
        } else {
            // Wrong move
            setIsCorrectMove(false);
            setShakingBoard(true);
            chessSounds.playWrong();
            
            setTimeout(() => {
                setIsCorrectMove(null);
                setShakingBoard(false);
            }, 500);
        }

        return true;
    };

    const makeComputerMove = (gameCopy: Chess, solutionMoves: string[], moveIndex: number) => {
        if (moveIndex >= solutionMoves.length) {
            // Check for puzzle completion after computer move
            if (puzzleProgress.length >= solutionMoves.length - 1) {
                setTimeout(() => {
                    setPuzzleCompleted(true);
                    chessSounds.playPuzzleComplete();
                    
                    if (activeRole && loggedInUserId) {
                        markPuzzleAsSolved(
                            activeRole.serviceProviderId,
                            subscriptionId!,
                            puzzleId!,
                            loggedInUserId,
                            activeRole.displayName || 'User'
                        );
                    }
                }, 300);
            }
            return;
        }

        const computerMove = solutionMoves[moveIndex];
        
        setTimeout(() => {
            try {
                const move = gameCopy.move(computerMove);
                if (move) {
                    setGame(new Chess(gameCopy.fen()));
                    setPuzzleProgress(prev => [...prev, move.san]);
                    setMoveHistory(prev => [...prev, { move: move.san, fen: gameCopy.fen() }]);
                    
                    if (move.captured) {
                        chessSounds.playCapture();
                    } else {
                        chessSounds.playMove();
                    }
                    
                    if (gameCopy.isCheck()) {
                        chessSounds.playCheck();
                    }

                    // Check for puzzle completion after computer move
                    if (puzzleProgress.length + 1 >= solutionMoves.length - 1) {
                        setTimeout(() => {
                            setPuzzleCompleted(true);
                            chessSounds.playPuzzleComplete();
                            
                            if (activeRole && loggedInUserId) {
                                markPuzzleAsSolved(
                                    activeRole.serviceProviderId,
                                    subscriptionId!,
                                    puzzleId!,
                                    loggedInUserId,
                                    activeRole.displayName || 'User'
                                );
                            }
                        }, 300);
                    }
                }
            } catch (error) {
                console.error('Computer move error:', error);
            }
        }, 500);
    };

    const resetPuzzle = () => {
        if (!puzzle) return;
        
        const newGame = new Chess(puzzle.fen);
        setGame(newGame);
        setPuzzleProgress([]);
        setIsCorrectMove(null);
        setPuzzleCompleted(false);
        setShowHint(false);
        setHintArrows([]);
        setMoveHistory([{ move: 'Start', fen: puzzle.fen }]);
        
        const turnColor = newGame.turn();
        setBoardOrientation(turnColor === 'w' ? 'white' : 'black');
    };

    const flipBoard = () => {
        setBoardOrientation(boardOrientation === 'white' ? 'black' : 'white');
    };

    if (loading) {
        return (
            <div className="puzzle-fullscreen-container">
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading puzzle...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!puzzle) {
        return (
            <div className="puzzle-fullscreen-container">
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <p className="text-gray-600 text-xl mb-4">Puzzle not found</p>
                        <Button
                            variant="contained"
                            onClick={() => navigate(-1)}
                            sx={{ backgroundColor: '#9333ea' }}
                        >
                            Go Back
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const solutionMoves = puzzle.moves.split(' ').filter(m => m && !m.match(/^\d+\.?$/) && !m.match(/^(1-0|0-1|1\/2-1\/2|\*)$/));
    const progressPercentage = (puzzleProgress.length / solutionMoves.length) * 100;

    return (
        <div className="puzzle-fullscreen-container">
            {/* Top Navigation */}
            <div className="puzzle-nav">
                <div className="puzzle-nav-left">
                    <Tooltip title="Back">
                        <IconButton onClick={() => navigate(-1)} className="nav-icon-btn">
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    <div className="puzzle-title">
                        <span className="puzzle-title-main">{puzzle.description || `Puzzle #${puzzle.id.slice(7, 13)}`}</span>
                        <span className="puzzle-title-meta">
                            {puzzle.complexity} • ⭐ {puzzle.rating} • {puzzle.category}
                        </span>
                    </div>
                </div>
                {/* Center Branding */}
                <div style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.125rem',
                    pointerEvents: 'none'
                }}>
                    <span style={{
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '0.5px'
                    }}>
                        Chequered
                    </span>
                    <span style={{
                        fontSize: '0.65rem',
                        color: '#6b7280',
                        fontWeight: 500,
                        whiteSpace: 'nowrap'
                    }}>
                        one stop Coaching platform by HoSaksham
                    </span>
                </div>
                <div className="puzzle-nav-right">
                    <Tooltip title={isSoundEnabled ? 'Mute' : 'Unmute'}>
                        <IconButton onClick={() => setIsSoundEnabled(!isSoundEnabled)} className="nav-icon-btn">
                            {isSoundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Close">
                        <IconButton onClick={() => navigate(-1)} className="nav-icon-btn">
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>

            {/* Main Content */}
            <div className="puzzle-main-content">
                {/* Left Sidebar */}
                <div className="puzzle-sidebar puzzle-sidebar-left">
                    <div className="sidebar-section">
                        <h3 className="sidebar-title">Puzzle Info</h3>
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Category</span>
                                <span className="info-value">{puzzle.category}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Rating</span>
                                <span className="info-value">⭐ {puzzle.rating}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Complexity</span>
                                <span className="info-value">{puzzle.complexity}</span>
                            </div>
                        </div>
                    </div>

                    <div className="sidebar-section">
                        <h3 className="sidebar-title">Progress</h3>
                        <div className="progress-bar-container">
                            <div className="progress-bar" style={{ width: `${progressPercentage}%` }}></div>
                        </div>
                        <p className="progress-text">{puzzleProgress.length} / {solutionMoves.length} moves</p>
                    </div>

                    {puzzleCompleted && (
                        <div className="sidebar-section completion-section">
                            <div className="completion-icon">🎉</div>
                            <h3 className="completion-title">Puzzle Solved!</h3>
                            <p className="completion-subtitle">Great job!</p>
                        </div>
                    )}

                    <div className="sidebar-section">
                        <h3 className="sidebar-title">Controls</h3>
                        <div className="controls-grid">
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => setShowHint(!showHint)}
                                disabled={puzzleCompleted}
                                startIcon={<LightbulbIcon />}
                                className="control-btn"
                                sx={{
                                    borderColor: showHint ? '#f59e0b' : '#d1d5db',
                                    color: showHint ? '#f59e0b' : '#6b7280',
                                    '&:hover': { borderColor: '#f59e0b', backgroundColor: '#fef3c7' }
                                }}
                            >
                                {showHint ? 'Hide Hint' : 'Show Hint'}
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={resetPuzzle}
                                startIcon={<RefreshIcon />}
                                className="control-btn"
                                sx={{
                                    borderColor: '#d1d5db',
                                    color: '#6b7280',
                                    '&:hover': { borderColor: '#9333ea', backgroundColor: '#f3e8ff' }
                                }}
                            >
                                Reset
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={flipBoard}
                                startIcon={<FlipCameraAndroidIcon />}
                                className="control-btn"
                                sx={{
                                    borderColor: '#d1d5db',
                                    color: '#6b7280',
                                    '&:hover': { borderColor: '#3b82f6', backgroundColor: '#dbeafe' }
                                }}
                            >
                                Flip Board
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Center - Chessboard */}
                <div className="puzzle-board-container">
                    <div className={`puzzle-board ${shakingBoard ? 'board-shake' : ''} ${puzzleCompleted ? 'board-completed' : ''}`}>
                        <Chessboard
                            position={game.fen()}
                            onPieceDrop={handleMove}
                            boardOrientation={boardOrientation}
                            customDarkSquareStyle={{ backgroundColor: '#b58863' }}
                            customLightSquareStyle={{ backgroundColor: '#f0d9b5' }}
                            customSquareStyles={hintSquare ? {
                                [hintSquare]: {
                                    backgroundColor: 'rgba(255, 255, 0, 0.4)',
                                    boxShadow: '0 0 20px 5px rgba(255, 193, 7, 0.8)',
                                }
                            } : {}}
                            arePiecesDraggable={!puzzleCompleted}
                        />
                    </div>
                    
                    {/* Status Indicator */}
                    {isCorrectMove !== null && !puzzleCompleted && (
                        <div className={`move-feedback ${isCorrectMove ? 'correct' : 'incorrect'}`}>
                            {isCorrectMove ? '✓ Correct!' : '✗ Try again'}
                        </div>
                    )}
                </div>

                {/* Right Sidebar */}
                <div className="puzzle-sidebar puzzle-sidebar-right">
                    <div className="sidebar-section">
                        <h3 className="sidebar-title">Move History</h3>
                        <div className="move-history">
                            {moveHistory.map((item, index) => (
                                <div key={index} className="move-history-item">
                                    <span className="move-number">{index === 0 ? '⚐' : index}</span>
                                    <span className="move-notation">{item.move}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {puzzle.hint && showHint && (
                        <div className="sidebar-section hint-section">
                            <h3 className="sidebar-title">💡 Hint</h3>
                            <p className="hint-text">{puzzle.hint}</p>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
                    20%, 40%, 60%, 80% { transform: translateX(8px); }
                }
                
                .board-shake {
                    animation: shake 0.5s ease-in-out;
                }
            `}</style>
        </div>
    );
};

export default PuzzleFullscreenPage;
